'use client';

/**
 * DateInput — the one date field (Complete_app §16.2 DatePicker, §17.1
 * "appropriate date inputs", §15.2 Indian formatting).
 *
 *     <FormField label="Hearing date" name="hearing_date" required>
 *       {(p) => <DateInput {...p} value={date} onChange={setDate} min={todayIso} />}
 *     </FormField>
 *
 * - The browser's own calendar (keyboard- and screen-reader-accessible on
 *   every platform) with the value kept as `YYYY-MM-DD` — the shape the API
 *   takes, and a calendar date that never shifts a day across zones.
 * - Under it, the date written out with the month as a word ("Thu, Oct 3,
 *   2026" / "Thu, 3 Oct 2026" by the viewer's locale) so the day/month order
 *   is never in doubt.
 * - `type="datetime-local"` via `withTime` for a moment; the read-out then
 *   includes the time.
 * - `onChange` receives the string ('' when cleared), not the event.
 */
import type { InputHTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { DESK_TIME_ZONE, formatDate, formatDateTime } from '@/lib/format';

import { cn } from './cn';
import { Input } from './Input';

export interface DateInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'size' | 'prefix'> {
  value: string | null | undefined;
  onChange: (value: string) => void;
  withTime?: boolean;
  /** Hide the written-out read-out (e.g. in a dense filter row). */
  hideReadout?: boolean;
  inputSize?: 'sm' | 'md';
}

/**
 * Today as `YYYY-MM-DD` in the given zone — for `min`/`max`. Defaults to the
 * browser's zone (`DESK_TIME_ZONE`); pass the user's own zone where known.
 * Global desk: never Asia/Kolkata.
 */
export function todayIsoDate(timeZone: string = DESK_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { value, onChange, withTime = false, hideReadout = false, inputSize = 'md', className, ...rest },
  ref,
) {
  const v = value ?? '';
  const readout = v
    ? withTime
      ? formatDateTime(v, { parts: { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' } })
      : formatDate(v, { parts: { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' } })
    : null;
  return (
    <div className={cn('space-y-1', className)}>
      <Input
        ref={ref}
        inputSize={inputSize}
        type={withTime ? 'datetime-local' : 'date'}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
      {!hideReadout && readout && readout !== '—' ? (
        <p aria-hidden className="text-xs text-fg-muted">
          {readout}
        </p>
      ) : null}
    </div>
  );
});

export interface DateRangeValue {
  from: string;
  to: string;
}

export interface DateRangeInputProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  /** Accessible names for the two inputs. */
  fromLabel?: string;
  toLabel?: string;
  min?: string;
  max?: string;
  inputSize?: 'sm' | 'md';
  className?: string;
  disabled?: boolean;
}

/** "From … to …" — the end can never be set before the start. */
export function DateRangeInput({
  value,
  onChange,
  fromLabel = 'From date',
  toLabel = 'To date',
  min,
  max,
  inputSize = 'sm',
  className,
  disabled,
}: DateRangeInputProps) {
  return (
    <div className={cn('flex flex-wrap items-start gap-2', className)}>
      <DateInput
        hideReadout
        aria-label={fromLabel}
        disabled={disabled}
        inputSize={inputSize}
        max={value.to || max}
        min={min}
        value={value.from}
        onChange={(from) => onChange({ from, to: value.to && from && value.to < from ? from : value.to })}
      />
      <span aria-hidden className="self-center text-sm text-fg-muted">
        to
      </span>
      <DateInput
        hideReadout
        aria-label={toLabel}
        disabled={disabled}
        inputSize={inputSize}
        max={max}
        min={value.from || min}
        value={value.to}
        onChange={(to) => onChange({ from: value.from, to })}
      />
    </div>
  );
}
